import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom, Subject } from 'rxjs';
import { Organization } from '../../../../organization/dto/Organization';
import { FundPerformance } from '../../../dto/FundPerformance';
import { PerformanceCardButtons } from '../pojo/PerformanceCardButtons';
import { HorNavbarService } from '../../../../../core/service/hor-navbar.service';
import { OrganizationsService } from '../../../../organization/components/service/organizations.service';
import { AuthService } from '../../../../auth/service/auth.service';
import { FundPerformanceBackendService } from '../../../service/fund-performance-backend.service';
import { NOTIFICATIONS, NotificationsService } from '../../../../../core/service/notifications.service';
import { takeUntil } from 'rxjs/operators';
import {
  FundPerformancePeriodEmitter,
  FundPerformanceShowModalEmitter,
} from '../fund-performance-high-level-table/fund-performance-high-level-table.component';
import { CardPopUpProperties } from '../pojo/CardPopUpProperties';
import { FpService } from '../../../service/fp.service';
import { PeriodService } from "../../../../../core/service/period.service";
import { FP_URLS_FULL_PATH } from "../pojo/FP_URLS";
import { PP_URLS } from "../../../../pp/component/base/dto/PpTab";
import { ActivatedRoute, Router } from "@angular/router";
import { ValueUtils } from '../../../../../core/service/value-utils/value-utils.service';
import { NavigationService } from '../../../../../core/service/navigation.service';

@Component({
  selector: 'app-fp-value-creation',
  templateUrl: './fp-value-creation.component.html',
  styleUrls: ['./fp-value-creation.component.scss'],
})
export class FpValueCreationComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  isAdmin = false;
  switcher = 1;
  selected = new FpSelected();
  tabs: FpTabs;
  show = new FpVcShow();

  fundPerformances: FundPerformance[];
  updatedFundPerformance: FundPerformance = undefined;
  withCarry = false;
  cardPopUp: CardPopUpProperties;
  currentUserType = 'viewer';
  math = Math;
  textListDeletePeriod: string[] = [];

  portfolioPerformanceCard: PerformanceCardButtons = {
    isTotal: true,
    isUnrealized: false,
    isRealized: false,
  };

  constructor(
    private navbarService: HorNavbarService,
    private periodService: PeriodService,
    private organizationsService: OrganizationsService,
    private authService: AuthService,
    private fundPerformanceService: FundPerformanceBackendService,
    private notifications: NotificationsService,
    private router: Router,
    private route: ActivatedRoute,
    private navigationService: NavigationService,
  ) {}

  ngOnInit(): void {
    this.initSubscriptions();
  }

  ngOnDestroy(): void {
    this.fundPerformances = [];
    this.portfolioPerformanceCard = {
      isTotal: true,
      isUnrealized: false,
      isRealized: false,
    };
    this.cardPopUp = new CardPopUpProperties();
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  async backToPrevUrl(): Promise<void> {
    await firstValueFrom(this.navigationService.navigatePreservingScroll(`${FP_URLS_FULL_PATH.FUND + '/' + PP_URLS.base + '/' + PP_URLS._overview}`));
  }

  setSelectedFundPerformance(): void {
    if (!this.fundPerformances?.length || !this.selected.period) {
      return;
    }
    this.selected.fp = this.fundPerformances.find((fp) => fp.period === this.selected.period);
    FpService.setTvpi(this.selected.fp);
    FpService.setDefaultIrrTotals(this.fundPerformances);

    if ((this.portfolioPerformanceCard.isRealized && !this.selected.fp.realizedCompaniesAmount) ||
      (this.portfolioPerformanceCard.isUnrealized && !this.selected.fp.activeCompaniesAmount)) {
      this.portfolioPerformanceCard = {
        isTotal: true,
        isUnrealized: false,
        isRealized: false,
      };
    }
    if (this.withCarry && !this.selected.fp.carryInterest) {
      this.withCarry = false;
    }
  }

  checkDistribution(): void {
    this.show.distribution = this.fundPerformances.find(
      (fundPerformance) => fundPerformance.distribution) !== undefined;
  }

  async deleteFundPerformance(shouldDelete: boolean): Promise<void> {
    this.show.modal.fpDelete = false;
    if (!shouldDelete) {
      return;
    }
    await firstValueFrom(this.fundPerformanceService.delete(
      this.selected.org.selectedFund.uuid, this.selected.period));
    this.navbarService.fetchAllFundPerformancesFromBackend();
    this.notifications.success(NOTIFICATIONS.FUNDPERFORMANCE_DELETE_SUCCESS);
    this.show.modal.fpUpdate = false;
  }

  getAllFundPerformancesByFundId(): void {
    if (!this.selected.org || !this.selected.org.selectedFund) {
      return;
    }
    this.fundPerformanceService.getAllFundPerformancesByFundUuid(
      this.selected.org.selectedFund.uuid,
      true, true,
    )
      .pipe(takeUntil(this._destroyStream))
      .subscribe({
        next: (f) => {
          if (!f) {
            return;
          }
          this.fundPerformances = f;
          this.afterFundPerformanceFetch();
          this.navbarService.setFundPerformances(f);
        }, error: (error) => console.error(error),
      });
  }

  parseShowModal(wrapper: FundPerformanceShowModalEmitter): void {
    switch (wrapper.type) {
      case 'update':
        this.showFpUpdateModal(wrapper.fp);
        break;
      case 'delete':
        this.showModalForDeleteFP(wrapper.fp);
        break;
      case 'bulk':
        this.show.modal.bulkUpload = true;
        break;
    }
  }

  parsePeriodEmitter(wrapper: FundPerformancePeriodEmitter): void {
    switch (wrapper.type) {
      case 'goToOverview':
        this.goToOverview(wrapper.fp);
        break;
      case 'unlock':
        this.blockPeriod(wrapper.fp.period, false);
        break;
      case 'block':
        this.blockPeriod(wrapper.fp.period, true);
        break;
      case 'toggleAll':
        this.blockUnlockAllPeriods();
        break;
      case 'isPredicted':
        this.changeIsBasedOnPrognosis(wrapper.fp);
        break;
      case 'visibility':
        this.updateVisibility(wrapper.fp);
        break;
    }
  }

  showFpUpdateModal(fundPerformance?: FundPerformance): void {
    const fundPerformanceForUpdate = fundPerformance ?? this.selected.fp;
    if (fundPerformanceForUpdate.blocked) return;
    if (ValueUtils.isValueAbsent(fundPerformanceForUpdate.carryInterestPercent) &&
      ValueUtils.isValueAbsent(fundPerformanceForUpdate.carryInterest)) {
      const lastNonNullCarryInterestPercent = this.fundPerformances
        .sort((a, b) => new Date(b.period).getTime() - new Date(a.period).getTime())
        .find((fp) => fp.carryInterestPercent !== null && fp.period < fundPerformanceForUpdate.period)?.carryInterestPercent;
      fundPerformanceForUpdate.carryInterestPercent = lastNonNullCarryInterestPercent || null;
    }
    this.updatedFundPerformance = fundPerformanceForUpdate;
    this.selected.fp = fundPerformanceForUpdate;
    this.show.modal.fpUpdate = true;
  }

  closeModalAfterUpdate(value: boolean): void {
    if (value) {
      this.getAllFundPerformancesByFundId();
    }
    this.show.modal.fpUpdate = false;
  }

  showModalForDeleteFP(fundPerformance: FundPerformance): void {
    this.selected.period = null;
    FpService.updateSelectedAfterNewPeriod(fundPerformance.period, this.selected, this.textListDeletePeriod);
    this.show.modal.fpDelete = true;
  }

  async changeIsBasedOnPrognosis(fp: FundPerformance): Promise<void> {
    this.selected.fp = await this.fundPerformanceService.update(fp);
    this.navbarService.updateFundPerformance(this.selected.fp);
    this.notifications.success(
      fp.isBasedOnPrognosis ? NOTIFICATIONS.PERIOD_IS_SET_PREDICTED_SUCCESS : NOTIFICATIONS.PERIOD_IS_UNSET_PREDICTED_SUCCESS,
    );
  }

  async blockPeriod(period: Date, value: boolean): Promise<void> {
    const fundPerformance = await firstValueFrom(this.fundPerformanceService.blockPeriod(
      this.selected.org.selectedFund.uuid, period, value,
    ).pipe(takeUntil(this._destroyStream)));
    this.navbarService.updateFundPerformance(fundPerformance);
    this.notifications.success(value ? NOTIFICATIONS.PERIOD_IS_BLOCKED_SUCCESS : NOTIFICATIONS.PERIOD_IS_UNLOCKED_SUCCESS);
  }

  blockUnlockAllPeriods(): void {
    this.fundPerformanceService.toggleIsBlockedInAllPeriods(this.selected.org.selectedFund.uuid).then(() => {
      this.navbarService.fetchAllFundPerformancesFromBackend();
      const areAllPeriodsBlocked = this.fundPerformances.every((fp) => fp.blocked);
      this.notifications.success(
        areAllPeriodsBlocked ? NOTIFICATIONS.ALL_PERIODS_ARE_UNLOCKED_SUCCESS : NOTIFICATIONS.ALL_PERIODS_ARE_BLOCKED_SUCCESS,
      );
    });
  }

  goToOverview(fp: FundPerformance): void {
    this.changePeriod(fp);
    this.router.navigate([ FP_URLS_FULL_PATH.FUND + `/` + PP_URLS.base + `/` + PP_URLS._overview], { queryParams: this.route.snapshot.queryParams });
  }

  copyPeriod(periodToCopy: Date): void {
    if (periodToCopy) {
      this.periodService.set(String(periodToCopy));
      this.navbarService.fetchAllFundPerformancesFromBackend();
    }
    this.show.modal.copyFp = false;
  }

  async changePeriod(fp: FundPerformance): Promise<void> {
    this.selected.fp = await this.fundPerformanceService.update(fp);
    this.periodService.set(String(fp.period));
    this.navbarService.setSelectedPeriod(this.selected.fp.period);
  }

  async updateVisibility(fundPerformance: FundPerformance): Promise<void> {
    const fp = await firstValueFrom(this.fundPerformanceService.changeVisibility(
      this.selected.org.selectedFund.uuid, fundPerformance.period, !fundPerformance.isVisible),
    );
    this.navbarService.updateFundPerformance(fp);
    this.notifications.success(
      fp.isVisible ? NOTIFICATIONS.PERIOD_IS_VISIBLE_SUCCESS : NOTIFICATIONS.PERIOD_IS_HIDDEN_SUCCESS,
    );
  }

  updateFpFromModal(fundPerformance: FundPerformance): void {
    this.navbarService.updateFundPerformance(fundPerformance);
  }

  private resetData(): void {
    this.selected.org = null;
    this.selected.fp = null;
    this.fundPerformances = [];
  }

  private initTabs(): void {
    this.tabs =
      {
        dataBehind: {
          selected: 'Fund performance',
          all: ['Fund performance', 'Portfolio details', 'Net IRR'],
        },
      };
  }

  private afterFundPerformanceFetch(): void {
    if (!this.fundPerformances.length) {
      this.show.emptyState = true;
      this.selected.fp = null;
      return;
    }
    this.checkDistribution();
    this.setSelectedFundPerformance();
  }

  private initSubscriptions(): void {
    this.organizationsService.selected$
      .pipe(takeUntil(this._destroyStream))
      .subscribe((org) => {
        this.resetData();
        if (!org) {
          return;
        }
        this.selected.org = org;
        this.cardPopUp = new CardPopUpProperties();
        this.initTabs();
      });

    this.navbarService.getNumberSwitcher
      .pipe(takeUntil(this._destroyStream))
      .subscribe((s) => {
        if (!s) {
          return;
        }
        this.switcher = s;
      });

    this.navbarService.getSelectedPeriod
      .pipe(takeUntil(this._destroyStream))
      .subscribe((period) => {
        FpService.updateSelectedAfterNewPeriod(period, this.selected, this.textListDeletePeriod);
        this.setSelectedFundPerformance();
      });

    this.navbarService.getPeriods
      .pipe(takeUntil(this._destroyStream))
      .subscribe((periods) => {
        if (!periods) {
          return;
        }
        this.selected.periods = periods;
      });

    this.authService.currentUserTypeForServices$.pipe(takeUntil(this._destroyStream)).subscribe(
      (userType) => {
        this.currentUserType = userType;
        this.isAdmin = this.currentUserType === 'admin';
      },
    );

    this.navbarService.getUpdateFundPerformance.pipe(takeUntil(this._destroyStream)).subscribe(
      (fp) => {
        if (!fp) {
          return;
        }
        const index = this.fundPerformances.findIndex((f) => f.id === fp.id);
        if (index !== -1) {
          this.fundPerformances[index] = fp; // Replace the element at the found index
        }
      },
    );

    this.navbarService.getFundPerformances
      .pipe(takeUntil(this._destroyStream))
      .subscribe((fp) => {
        if (!fp || fp === this.fundPerformances) {
          return;
        }
        this.fundPerformances = fp;
        this.afterFundPerformanceFetch();
      });

    this.navbarService.getFetchAllFundPerformancesFromBackend.pipe(takeUntil(this._destroyStream)).subscribe(
      (b) => {
        if (!b) {
          return;
        }
        this.getAllFundPerformancesByFundId();
      },
    );
  }
}

const DataBehindTabs = ['Fund performance', 'Portfolio details', 'Net IRR'] as const;

interface FpTabs {
  dataBehind: {
    all: typeof DataBehindTabs[number][];
    selected: typeof DataBehindTabs[number];
  };
}

class FpVcShow {
  emptyState = false;
  valueCreationCharts = false;
  modal = new ShowModal();
  distribution = false;
}

class ShowModal {
  copyFp = false;
  fpDelete = false;
  bulkUpload = false;
  fpUpdate = false;
}

export class FpSelected {
  org: Organization;
  fp: FundPerformance;
  period: Date;
  periods: Date[];
}
